import type { EntityAttribute } from '../types/config';

const ATTR_PREFIX = 'attributes.';

/**
 * Minimal field-config shape that both `PromoFieldConfig` and `EventFieldConfig`
 * structurally satisfy. The codec only needs the form-field linkage + transform
 * hints; it stays agnostic of the richer per-level field types.
 */
export interface AttributeFieldConfig {
    name?: string;
    apiKey?: string | null;
    responseKey?: string | null;
    transform?: string | null;
    responseTransform?: string | null;
    onEmpty?: string;
}

export type TransformMap = Record<string, (v: unknown) => unknown>;

const isEmpty = (v: unknown): boolean => v === undefined || v === null || v === '';

export function getAttributesByLevel(entityAttributes: EntityAttribute[], level: string): EntityAttribute[] {
    return entityAttributes.filter((a) => a.level === level);
}

// Map attribute key -> field via the `attributes.<key>` path on the given
// property (`apiKey` for writes, `responseKey` for reads).
function buildFieldMap(
    fields: readonly AttributeFieldConfig[],
    keyProp: 'apiKey' | 'responseKey',
): Map<string, AttributeFieldConfig> {
    const map = new Map<string, AttributeFieldConfig>();
    for (const field of fields) {
        const path = field[keyProp];
        if (typeof path === 'string' && path.startsWith(ATTR_PREFIX)) {
            map.set(path.slice(ATTR_PREFIX.length), field);
        }
    }
    return map;
}

// Resolve which attribute keys to emit/read for a level. Returns the union of
// backend-declared `entity_attributes` keys and the keys derived from the field
// configs' `attributes.*` paths: declared keys come first (preserving their
// `default_value`); field-config-only keys are appended (default `undefined`).
// This keeps the FE self-sufficient — a field config can introduce a new
// attribute key without waiting for the backend to declare it — while still
// honouring backend-declared defaults/ordering.
function resolveAttributeKeys(
    entityAttributes: EntityAttribute[],
    fieldMap: Map<string, AttributeFieldConfig>,
    level: string,
): { key: string; defaultValue: unknown }[] {
    const result: { key: string; defaultValue: unknown }[] = [];
    const seen = new Set<string>();
    for (const a of getAttributesByLevel(entityAttributes, level)) {
        result.push({ key: a.key, defaultValue: a.default_value ?? undefined });
        seen.add(a.key);
    }
    for (const key of fieldMap.keys()) {
        if (!seen.has(key)) result.push({ key, defaultValue: undefined });
    }
    return result;
}

/**
 * Builds the nested `attributes` object for a request payload. Driven by
 * `entity_attributes` (filtered by `level`); each attribute's form value +
 * transform + onEmpty behaviour are resolved from the matching field config
 * (linked via `apiKey: 'attributes.<key>'`).
 */
export function buildAttributesPayload(
    entityAttributes: EntityAttribute[],
    fields: readonly AttributeFieldConfig[],
    formValues: Record<string, unknown>,
    level: string,
    transforms: TransformMap,
): Record<string, unknown> {
    const fieldMap = buildFieldMap(fields, 'apiKey');
    const descriptors = resolveAttributeKeys(entityAttributes, fieldMap, level);
    const attributes: Record<string, unknown> = {};

    for (const { key, defaultValue } of descriptors) {
        const field = fieldMap.get(key);
        const formKey = field?.name ?? key;
        let raw = formValues[formKey];
        if (raw === undefined && !field) raw = defaultValue;

        if (isEmpty(raw)) {
            // Only `emptyString` materialises an empty value; everything else is omitted.
            if (field?.onEmpty === 'emptyString') attributes[key] = '';
            continue;
        }

        const transformName = field?.transform ?? undefined;
        attributes[key] = transformName && transforms[transformName] ? transforms[transformName](raw) : raw;
    }

    return attributes;
}

/**
 * Reverse of `buildAttributesPayload`: maps a response `attributes` dict back to
 * form values keyed by each attribute's form-field name (linked via
 * `responseKey: 'attributes.<key>'`).
 */
export function readAttributesToForm(
    entityAttributes: EntityAttribute[],
    fields: readonly AttributeFieldConfig[],
    attributesObj: Record<string, unknown> | null | undefined,
    level: string,
    transforms: TransformMap,
): Record<string, unknown> {
    if (!attributesObj) return {};
    const fieldMap = buildFieldMap(fields, 'responseKey');
    const descriptors = resolveAttributeKeys(entityAttributes, fieldMap, level);
    const values: Record<string, unknown> = {};

    for (const { key } of descriptors) {
        const field = fieldMap.get(key);
        const raw = attributesObj[key];
        if (raw === undefined || raw === null) continue;
        const transformName = field?.responseTransform ?? undefined;
        const formKey = field?.name ?? key;
        values[formKey] = transformName && transforms[transformName] ? transforms[transformName](raw) : raw;
    }

    return values;
}
